import React, { useEffect, useRef, useState } from "react";
import { Form, Flex } from "antd";
import type { UploadFile } from "antd";
import dayjs from "dayjs";
import type { ConceptData, QuoteData, QuoteProduct, QuoteConceptPayload } from "../types";
import { useFetchClients } from "../../clients/hooks/useFetchClients";
import { useFetchConcepts } from "../../concepts/hooks/useFetchConcepts";
import { useUpdateQuote } from "../hooks/useUpdateQuote";
import { useUpdateQuoteClient } from "../hooks/useUpdateQuoteClient";
import { useAddQuoteConcept } from "../hooks/useAddQuoteConcept";
import { useRemoveQuoteConcept } from "../hooks/useRemoveQuoteConcept";
import { useQuoteSignatureImage } from "../hooks/useQuoteSignatureImage";
import { useUploadFile } from "../../../lib/files/hooks/useUploadFile";
import { URL_FILES } from "../../../constans/apis";
import { storageName } from "../../../constans/auth";
import openNotification from "../../../utils/openNotification";
import QuoteForm from "./QuoteForm";
import type { SignaturePadHandle } from "./SignaturePad";

/** Nombre completo del usuario logeado, leído del JWT decodificado (ver useLogin.ts). */
function loggedUserFullName(): string {
  try {
    const raw = localStorage.getItem(storageName);
    if (!raw) return "";
    const user = JSON.parse(raw);
    return [user?.name, user?.lastname, user?.lastname2].filter(Boolean).join(" ");
  } catch {
    return "";
  }
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

interface EditQuotePanelProps {
  quote: QuoteData;
  onCancel: () => void;
  /** Called after a successful save so the parent can refresh the displayed quote */
  onSaved?: (updated: Partial<QuoteData>) => void;
}

const EditQuotePanel: React.FC<EditQuotePanelProps> = ({ quote, onCancel, onSaved }) => {
  const [form] = Form.useForm();

  // ── Concepts state ────────────────────────────────────────────────────────
  const [localConcepts, setLocalConcepts] = useState<ConceptData[]>(quote.concepts ?? []);
  const [showConceptSelector, setShowConceptSelector] = useState(false);

  // ── Products state ────────────────────────────────────────────────────────
  // En edición las líneas se administran desde ProductsSection (QuoteDetails);
  // aquí solo se pasan para poder calcular los importes de la cotización.
  const selectedProducts: QuoteProduct[] = quote.products ?? [];
  const [fileList] = useState<UploadFile[]>([]);

  // ── Remote data ───────────────────────────────────────────────────────────
  const { data: clients = [], isLoading: isLoadingClients } = useFetchClients();
  const { data: concepts = [], isLoading: isLoadingConcepts } = useFetchConcepts();

  // ── Signature state ───────────────────────────────────────────────────────
  const [signerName, setSignerName] = useState(quote.signature_name || loggedUserFullName());
  const [isRedrawing, setIsRedrawing] = useState(!quote.uuid_signature_quote);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const signaturePadRef = useRef<SignaturePadHandle>(null);
  const { data: currentSignatureSrc, isLoading: isLoadingSignature } = useQuoteSignatureImage(
    quote.uuid_signature_quote
  );

  // ── Mutations ─────────────────────────────────────────────────────────────
  const { mutate: updateQuote, isPending: isUpdatingQuote } = useUpdateQuote();
  const { mutate: updateClient, isPending: isUpdatingClient } = useUpdateQuoteClient();
  const { mutate: addConcept, isPending: isAddingConcept } = useAddQuoteConcept();
  const { mutate: removeConcept, isPending: isRemovingConcept } = useRemoveQuoteConcept();
  const { mutateAsync: uploadSignatureFile } = useUploadFile();

  const isSubmitting = isUpdatingQuote || isUpdatingClient || isUploadingSignature;

  // ── Pre-populate form ─────────────────────────────────────────────────────
  useEffect(() => {
    form.setFieldsValue({
      id_client: quote.id_client,
      description_quote: quote.description_quote,
      quote_wave: quote.quote_wave,
      id_concept: quote.concepts?.[0]?.id_concept ?? undefined,
      // total_quote / iva_quote los calcula QuoteForm a partir de las líneas.
      date_quote: dayjs(quote.date_quote),
      date_expiration: dayjs(quote.date_expiration),
    });
    setLocalConcepts(quote.concepts ?? []);
  }, [quote, form]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const updatedFields: Partial<QuoteData> = {};

      // --- Signature: solo se sube si el usuario dibujó un trazo nuevo ---
      let uuidSignatureQuote = quote.uuid_signature_quote;
      const newSignatureDataUrl = isRedrawing ? signaturePadRef.current?.getDataUrl() : null;
      if (newSignatureDataUrl) {
        setIsUploadingSignature(true);
        try {
          const file = dataUrlToFile(newSignatureDataUrl, `firma-cotizacion-${quote.id_quote}.png`);
          const resFile = await uploadSignatureFile({ file, endpoint: URL_FILES, token: TOKEN_FILES });
          const uuid = resFile.data?.UUID_file;
          if (resFile.status !== 201 || !uuid) {
            openNotification("error", "Error", resFile.message || "No se pudo guardar la firma");
            return;
          }
          uuidSignatureQuote = uuid;
        } catch {
          openNotification("error", "Error", "No se pudo guardar la firma");
          return;
        } finally {
          setIsUploadingSignature(false);
        }
      }

      // --- Base quote update ---
      updateQuote(
        {
          id_quote: quote.id_quote,
          description_quote: values.description_quote,
          quote_wave: values.quote_wave,
          total_quote: Number(values.total_quote),
          iva_quote: Number(values.iva_quote),
          date_quote: values.date_quote.format("YYYY-MM-DD"),
          date_expiration: values.date_expiration.format("YYYY-MM-DD"),
          uuid_file_quote: quote.uuid_file_quote,
          id_client: values.id_client,
          id_contract: quote.id_contract,
          signature_name: signerName.trim() || undefined,
          uuid_signature_quote: uuidSignatureQuote,
        },
        {
          onSuccess: () => {
            updatedFields.description_quote = values.description_quote;
            updatedFields.quote_wave = values.quote_wave;
            updatedFields.total_quote = Number(values.total_quote);
            updatedFields.iva_quote = Number(values.iva_quote);
            updatedFields.date_quote = values.date_quote.format("YYYY-MM-DD");
            updatedFields.date_expiration = values.date_expiration.format("YYYY-MM-DD");
            updatedFields.signature_name = signerName.trim() || undefined;
            updatedFields.uuid_signature_quote = uuidSignatureQuote;

            // --- Client update (only if changed) ---
            if (values.id_client !== quote.id_client) {
              updateClient(
                { id_quote: quote.id_quote, id_client: values.id_client },
                {
                  onSuccess: () => {
                    updatedFields.id_client = values.id_client;
                    openNotification("success", "Éxito", "Cotización actualizada correctamente");
                    onSaved?.(updatedFields);
                    onCancel();
                  },
                  onError: (err) => openNotification("error", "Error al actualizar cliente", err.message),
                }
              );
            } else {
              openNotification("success", "Éxito", "Cotización actualizada correctamente");
              onSaved?.(updatedFields);
              onCancel();
            }
          },
          onError: (err) => openNotification("error", "Error al guardar", err.message),
        }
      );
    } catch {
      // validateFields rejected
    }
  };

  

  return (
    <Flex vertical gap={16}>
      {/* ── Quote base fields (incluye la Firma, ver QuoteForm) ──────── */}
      <QuoteForm
        form={form}
        mode="edit"
        initialQuote={quote}
        selectedProducts={selectedProducts}
        onProductChange={() => {}}
        onAmountChange={() => {}}
        onRemoveProduct={() => {}}
        fileList={fileList}
        onFileListChange={() => {}}
        onUuidFileReady={() => {}}
        clients={clients}
        concepts={concepts as ConceptData[]}
        isLoadingClients={isLoadingClients}
        isLoadingConcepts={isLoadingConcepts}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        hideConcepts={true}
        hideProducts={true}
        signerName={signerName}
        onSignerNameChange={setSignerName}
        signaturePadRef={signaturePadRef}
        isRedrawingSignature={isRedrawing}
        onToggleRedrawSignature={setIsRedrawing}
        currentSignatureSrc={currentSignatureSrc}
        isLoadingSignature={isLoadingSignature}
        hasExistingSignature={!!quote.uuid_signature_quote}
      />
    </Flex>
  );
};

export default EditQuotePanel;
